// api.jsx
// Base URL for your API
export const BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3333";

// Default fetch options for all API calls
const defaultOptions = {
  credentials: "include", // Always include credentials
  headers: {
    "Content-Type": "application/json",
  },
};

// Fetch categories for the YearContext
export const fetchCategories = async () => {
  const userData = JSON.parse(localStorage.getItem("userData"));
  const token = userData?.token;
  const response = await fetch(`${BASE_URL}/goal/categories`, {
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await response.json();
  if (response.ok) {
    return data.categories;
  } else {
    throw new Error(data.error || "Failed to fetch categories");
  }
};

// Fetch currency for the YearContext
export const fetchCurrencies = async () => {
  const response = await fetch(`${BASE_URL}/wishlist/currencies`, {
    ...defaultOptions,
    credentials: "include",
  });
  const data = await response.json();
  if (response.ok) {
    return data.currencies;
  } else {
    console.error("Error fetching currencies:", data.error);
    throw new Error(data.error || "Failed to fetch currencies");
  }
};

// Logout function
export const logout = async () => {
  const response = await fetch(`${BASE_URL}/logout`, {
    method: "POST",
    ...defaultOptions,
  });
  return response;
};

// Check authentication for the AuthContext
export const checkAuth = async () => {
  // console.log("�� Starting checkAuth...");
  try {
    // console.log("🔍 Making request to /getuser...");
    const response = await fetch(`${BASE_URL}/getuser`, {
      ...defaultOptions,
    });

    // console.log("📡 Server response status:", response.status);

    if (!response.ok) {
      // Try to parse as JSON first
      let errorData;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        errorData = await response.json();
      } else {
        // If not JSON, get the text and create a generic error
        const text = await response.text();
        console.error("Non-JSON error response:", text);
        errorData = { message: "Server error occurred" };
      }

      // console.log("❌ Server error response:", errorData);

      // Always remove token for unauthorized/forbidden responses
      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem("userData");
        // throw new Error("Session expired. Please log in again.");
        return;
      }

      throw new Error(errorData.message || "Authentication failed");
    }

    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Invalid response format from server");
    }

    const data = await response.json();
    // console.log("✅ Authentication successful:", data);

    // Validate the response data
    if (!data || !data.data) {
      // console.log("❌ Invalid response data format");
      await logout();
      throw new Error("Invalid response data");
    }

    return data;
  } catch (error) {
    // console.error("❌ CheckAuth error:", error);
    // Always remove token for unauthorized/forbidden errors
    if (
      error.message &&
      (error.message.toLowerCase().includes("unauth") ||
        error.message.toLowerCase().includes("forbidden") ||
        error.message.toLowerCase().includes("expired") ||
        error.message.toLowerCase().includes("invalid"))
    ) {
      await logout();
    }
    throw error;
  }
};

// Fetch goals for a specific year
export const fetchGoals = async () => {
  const response = await fetch(`${BASE_URL}/goal/goals`, {
    credentials: "include",
  });
  if (!response.ok) {
    return [];
  }
  return await response.json();
};

// Register a new user
export const registerUser = async (userData) => {
  try {
    const response = await fetch(`${BASE_URL}/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });

    const responseData = await response.text(); // Read the response as plain text
    // console.log("Raw response:", responseData); // Log the raw response

    // Check if the response is valid JSON
    let parsedData;
    try {
      parsedData = JSON.parse(responseData);
    } catch (error) {
      console.error(error);
      throw new Error("Received an invalid JSON response");
    }

    if (!response.ok) {
      throw new Error(parsedData.message || "Registration failed");
    }

    return parsedData;
  } catch (error) {
    console.error("Registration failed:", error);
    throw error;
  }
};

// User login and fetch related data
export const loginUser = async (userData) => {
  // console.log("🔄 Starting login process...");
  try {
    // console.log("🔍 Making login request...");
    const response = await fetch(`${BASE_URL}/login`, {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(userData),
    });

    // console.log("📡 Login response status:", response.status);

    if (!response.ok) {
      const errorData = await response.json();
      console.log("❌ Login error response:", errorData);
      throw new Error(errorData.message || "Login failed");
    }

    const data = await response.json();
    console.log("✅ Login successful, received data:", {
      ...data,
      token: data.token ? "exists" : "missing",
    });

    return { userData: data.userData };
  } catch (error) {
    console.error("❌ Login error:", error);
    throw error;
  }
};

// Fetch wishlist items
export const fetchWishlist = async () => {
  const response = await fetch(`${BASE_URL}/wishlist/wish`, {
    credentials: "include",
  });
  if (!response.ok) {
    return [];
  }
  return await response.json();
};

// Fetch public wishlist items
export const fetchPublicWishlist = async (token, year) => {
  const response = await fetch(`${BASE_URL}/wishlist/public/${year}/${token}`);
  if (!response.ok) {
    const body = await response.json();
    return body;
  }
  return await response.json();
};

// Add a wish
export const addWish = async (wishData, userId) => {
  try {
    // Add user_id to FormData if not already present
    if (!wishData.has("user_id")) {
      wishData.append("user_id", userId);
    }

    const response = await fetch(`${BASE_URL}/wishlist/addwish`, {
      method: "POST",
      body: wishData,
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `Wish submission failed: ${response.statusText}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error submitting wish:", error);
    throw error;
  }
};

// Fetch milestones
export const fetchMilestones = async () => {
  try {
    const response = await fetch(`${BASE_URL}/goal/milestones`, {
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch milestones");
    }

    return data;
  } catch (err) {
    console.error("Error fetching milestones:", err.message);
    throw err; // Re-throw the error for handling in the calling component
  }
};

//Add a quote
export const submitQuote = async (quote, selectedYear) => {
  try {
    const response = await fetch(`${BASE_URL}/year/quote`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ quote, year: selectedYear }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to save quote");
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error submitting quote:", error);
    throw error;
  }
};

// Add theme
export const submitTheme = async (theme, userId, selectedYear) => {
  try {
    const response = await fetch(`${BASE_URL}/year/theme`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ theme, year: selectedYear }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to save theme");
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error submitting theme:", error);
    throw error;
  }
};

// Add letter
export const submitLetter = async (
  letter,
  titleLetter,
  userId,
  selectedYear
) => {
  try {
    const response = await fetch(`${BASE_URL}/year/letter`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        letter,
        title_letter: titleLetter,
        user_id: userId,
        year: selectedYear,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to save letter");
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error submitting letter:", error);
    throw error;
  }
};

// Delete a goal
export const deleteGoal = async (goalId) => {
  console.log("🗑️ Attempting to delete goal:", goalId);

  const response = await fetch(`${BASE_URL}/goal/removegoal/${goalId}`, {
    method: "DELETE",
    credentials: "include",
  });

  console.log("📡 Delete response status:", response.status);

  const data = await response.json();
  console.log("📦 Delete response data:", data);

  if (!response.ok) {
    console.error("❌ Delete failed:", data);
    throw new Error(data.message || "Failed to delete goal");
  }

  console.log("✅ Goal deleted successfully");
  return data;
};

// Delete a milestone
export const deleteMilestone = async (milestoneId) => {
  const response = await fetch(
    `${BASE_URL}/goal/removemilestone/${milestoneId}`,
    {
      method: "DELETE",
      credentials: "include",
    }
  );

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Failed to delete milestone");
  }

  return data;
};

// Delete user account
export const deleteUser = async () => {
  try {
    const response = await fetch(`${BASE_URL}/deleteuser`, {
      method: "DELETE",
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to delete user account");
    }

    return data;
  } catch (error) {
    console.error("Error during account deletion:", error);
    throw error;
  }
};

// Add goal
export const addGoal = async (goalData) => {
  console.log("🎯 Starting goal addition...");
  console.log("📝 Goal data being sent:", {
    title: goalData.get("goal_title"),
    year: goalData.get("goal_year"),
    category: goalData.get("goal_category"),
    milestones: JSON.parse(goalData.get("milestones")),
    hasImage: !!goalData.get("goal_img"),
  });

  try {
    const response = await fetch(`${BASE_URL}/goal/addgoal`, {
      method: "POST",
      body: goalData,
      credentials: "include",
    });

    console.log("📡 Server response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (jsonError) {
        console.error(jsonError);
        // If parsing fails, use the raw text as the message
        errorData = { message: errorText };
      }
      console.log("❌ Server error response:", errorData);
      throw new Error(
        `Goal submission failed: ${errorData.message || response.statusText}`
      );
    }

    const data = await response.json();
    console.log("✅ Goal added successfully:", data);
    return data;
  } catch (err) {
    console.error("❌ Error submitting goal:", err);
    throw err;
  }
};

// Fetch Quote
export const fetchQuote = async () => {
  const response = await fetch(`${BASE_URL}/year/quotes`, {
    credentials: "include",
    ...defaultOptions,
  });
  if (!response.ok) {
    throw new Error("Failed to fetch quotes");
  }
  return await response.json();
};

// Delete Quote
export const deleteQuote = async (quoteId) => {
  try {
    const response = await fetch(`${BASE_URL}/year/removequote/${quoteId}`, {
      method: "DELETE",
      credentials: "include",
      ...defaultOptions,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to delete quote");
    }

    return data;
  } catch (error) {
    console.error("Error deleting quote:", error.message);
    throw error;
  }
};

// Fetch Theme
export const fetchTheme = async () => {
  const response = await fetch(`${BASE_URL}/year/themes`, {
    credentials: "include",
    ...defaultOptions,
  });
  if (!response.ok) {
    throw new Error("Failed to fetch themes");
  }
  return await response.json();
};

// Delete Theme
export const deleteTheme = async (themeId) => {
  try {
    const response = await fetch(`${BASE_URL}/year/removetheme/${themeId}`, {
      method: "DELETE",
      credentials: "include",
      ...defaultOptions,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to delete theme");
    }

    return data;
  } catch (error) {
    console.error("Error deleting theme:", error.message);
    throw error;
  }
};

// Fetch letter
export const fetchLetter = async () => {
  const response = await fetch(`${BASE_URL}/year/letters`, {
    credentials: "include",
    ...defaultOptions,
  });
  if (!response.ok) {
    throw new Error("Failed to fetch letters");
  }
  return await response.json();
};

// Delete Letter
export const deleteLetter = async (letterId) => {
  try {
    const response = await fetch(`${BASE_URL}/year/removeletter/${letterId}`, {
      method: "DELETE",
      credentials: "include",
      ...defaultOptions,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to delete letter");
    }

    return data;
  } catch (error) {
    console.error("Error deleting letter:", error.message);
    throw error;
  }
};

// Update Milestone Status
export const updateMilestoneStatus = async (milestoneId, newStatus) => {
  try {
    const response = await fetch(
      `${BASE_URL}/goal/editmilestonestatus/${milestoneId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status:
            typeof newStatus === "boolean" ? newStatus : newStatus === "true",
          status_changed_at: new Date().toISOString(), // Add the timestamp when status changes
        }),
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to update milestone status: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating milestone status:", error.message);
    throw error;
  }
};

// Update Wish Status
export const updateWishStatus = async (wishId, newStatus) => {
  try {
    const response = await fetch(`${BASE_URL}/wishlist/editstatus/${wishId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        status: newStatus, // Sends true/false as a boolean
      }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to update wish status: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating wish status:", error.message);
    throw error;
  }
};

//Add a note
export const submitNote = async (
  note_title,
  note_description,
  note_date,
  userId,
  selectedYear
) => {
  try {
    const response = await fetch(`${BASE_URL}/year/note`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        note_title,
        note_description,
        note_date,
        user_id: userId,
        year: selectedYear,
      }),
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to save note");
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error submitting note:", error);
    throw error;
  }
};

// Fetch notes
export const fetchNote = async () => {
  const response = await fetch(`${BASE_URL}/year/notes`, {
    ...defaultOptions,
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Failed to fetch notes");
  }
  return await response.json();
};

// Delete Note
export const deleteNote = async (noteId) => {
  try {
    const response = await fetch(`${BASE_URL}/year/removenote/${noteId}`, {
      method: "DELETE",
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to delete note");
    }

    return data;
  } catch (error) {
    console.error("Error deleting note:", error.message);
    throw error;
  }
};

// update profile personal details
export const updateProfilePersonalDetails = async (userId, userData) => {
  try {
    const response = await fetch(`${BASE_URL}/update-personal-details`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId, ...userData }),
      credentials: "include",
    });

    const contentType = response.headers.get("Content-Type");
    if (!response.ok) {
      throw new Error(`Failed to update profile. Status: ${response.status}`);
    }

    if (contentType && contentType.includes("application/json")) {
      const responseBody = await response.json();
      console.log("API response:", responseBody);
      return responseBody;
    } else {
      throw new Error("Unexpected response format. Expected JSON.");
    }
  } catch (error) {
    console.error("Error updating profile:", error);
    throw new Error("Failed to update profile. Please try again later.");
  }
};

// update login personal details
export const updateProfileLoginDetails = async (userId, userData) => {
  try {
    const response = await fetch(`${BASE_URL}/update-login-details`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId, ...userData }),
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to update profile");
    }

    return data;
  } catch (error) {
    console.error("Error updating profile:", error);
    throw error;
  }
};

// update password
export const updatePassword = async (userId, userData) => {
  try {
    const response = await fetch(`${BASE_URL}/update-password`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId, ...userData }),
      credentials: "include",
    });

    // If we get a redirect response, return it
    if (response.redirected) {
      // Clear local storage before redirecting
      localStorage.removeItem("userData");
      return { redirect: response.url };
    }

    const contentType = response.headers.get("Content-Type");
    if (!response.ok) {
      throw new Error(`Failed to update password. Status: ${response.status}`);
    }

    if (contentType && contentType.includes("application/json")) {
      const responseBody = await response.json();
      console.log("API response:", responseBody);
      return responseBody;
    } else {
      throw new Error("Unexpected response format. Expected JSON.");
    }
  } catch (error) {
    console.error("Error updating password:", error);
    throw new Error("Failed to update password. Please try again later.");
  }
};

// Update Milestone
export const updateMilestone = async (milestoneId, updatedData) => {
  try {
    const response = await fetch(
      `${BASE_URL}/goal/editmilestone/${milestoneId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedData),
        credentials: "include",
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.message || `Failed to update milestone: ${response.statusText}`
      );
    }

    return data.milestone; // Return the updated milestone data
  } catch (error) {
    console.error("Error updating milestone:", error.message);
    throw error;
  }
};

// Update Goal Favourite Status
export const updateGoalFavourite = async (goalId, isFavourite) => {
  try {
    const response = await fetch(
      `${BASE_URL}/goal/editgoal/${goalId}/favourite`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ favourite: isFavourite }),
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to update goal favourite status: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating goal favourite status:", error.message);
    throw error;
  }
};

// Delete wish
export const deleteWish = async (wishId) => {
  try {
    const response = await fetch(`${BASE_URL}/wishlist/removewish/${wishId}`, {
      method: "DELETE",
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to delete wish");
    }

    return data;
  } catch (error) {
    console.error("Error deleting wish:", error.message);
    throw error;
  }
};

// edit Quote
export const updateQuote = async (quoteId, updatedQuoteData) => {
  try {
    const response = await fetch(`${BASE_URL}/year/editquote/${quoteId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updatedQuoteData),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to update quote: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error("Error updating quote:", error.message);
    throw error;
  }
};

// edit theme
export const updateTheme = async (themeId, updatedThemeData) => {
  try {
    const response = await fetch(`${BASE_URL}/year/edittheme/${themeId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updatedThemeData),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to update theme: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error("Error updating theme:", error.message);
    throw error;
  }
};

// edit letter
export const updateLetter = async (letterId, updatedLetterData) => {
  try {
    const response = await fetch(`${BASE_URL}/year/editletter/${letterId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...updatedLetterData,
        user_id: updatedLetterData.user_id, // Ensure user_id is included
      }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to update letter: ${response.statusText}`);
    }

    const result = await response.json();
    return result.data;
  } catch (error) {
    console.error("Error updating letter:", error.message);
    throw error;
  }
};

// edit note
export const updateNote = async (noteId, updatedNoteData) => {
  try {
    const response = await fetch(`${BASE_URL}/year/editnote/${noteId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updatedNoteData),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to update note: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error updating note:", error.message);
    throw error;
  }
};

export const updateWish = async (
  wishId,
  updatedWishData,
  newImageFile = null
) => {
  try {
    const formData = new FormData();
    formData.append("title", updatedWishData.title);
    formData.append("url", updatedWishData.url);
    formData.append("price", updatedWishData.price);
    formData.append("currency", updatedWishData.currency);
    formData.append("year", updatedWishData.year);

    if (newImageFile) {
      formData.append("wish_img", newImageFile);
    } else {
      formData.append("img", updatedWishData.img);
    }

    const response = await fetch(`${BASE_URL}/wishlist/editwish/${wishId}`, {
      method: "PATCH",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to update wish: ${errorData.message || response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating wish:", error.message);
    throw error;
  }
};

export const updateGoal = async (formData, goalId) => {
  try {
    const response = await fetch(`${BASE_URL}/goal/editgoal/${goalId}`, {
      method: "PATCH",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Failed to update goal: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error updating goal:", error.message);
    throw error;
  }
};

export const resendVerificationEmail = async (email) => {
  const response = await fetch(`${BASE_URL}/resend-verification`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Failed to resend verification email.");
  }

  return data;
};

export const forgotPassword = async ({ email }) => {
  const response = await fetch(`${BASE_URL}/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Something went wrong.");
  }

  return response.json();
};

export const registerResendLink = async (token) => {
  const response = await fetch(`${BASE_URL}/resend-email/${token}`);
  if (!response.ok) {
    return [];
  }
  return await response.json();
};

export const resetPassword = async (token, newPassword) => {
  try {
    const response = await fetch(`${BASE_URL}/reset-password/${token}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ newPassword }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to reset password.");
    }

    const res = await response.json();

    // If we get a redirect response, return it
    if (res.redirect) {
      return res;
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to reset password.");
    }

    return data;
  } catch (error) {
    console.error("Reset password error:", error);
    throw error;
  }
};

// Cancel subscription
export const cancelSubscription = async (stripeCustomerId) => {
  try {
    const response = await fetch(`${BASE_URL}/stripe/cancel-subscription`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        stripeCustomerId,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to cancel subscription");
    }

    return await response.json();
  } catch (error) {
    console.error("Error cancelling subscription:", error);
    throw error;
  }
};

// Create Stripe Customer Portal session
export const createPortalSession = async () => {
  try {
    const response = await fetch(`${BASE_URL}/stripe/create-portal-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to create portal session");
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating portal session:", error);
    throw error;
  }
};

// Create Stripe Checkout session
export const createCheckoutSession = async (priceId) => {
  try {
    const response = await fetch(`${BASE_URL}/stripe/create-checkout-session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        priceId,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to create portal session");
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating portal session:", error);
    throw error;
  }
};

// Fetch notifications for the current user
export const fetchNotifications = async () => {
  try {
    const response = await fetch(`${BASE_URL}/api/notifications`, {
      ...defaultOptions,
      headers: {
        ...defaultOptions.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to fetch notifications");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

// Generate test notifications
export const generateTestNotifications = async () => {
  try {
    const response = await fetch(`${BASE_URL}/api/notifications/test`, {
      ...defaultOptions,
      method: "POST",
      headers: {
        ...defaultOptions.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.details || "Failed to generate test notifications"
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error generating test notifications:", error);
    throw error;
  }
};

// Mark a notification as read
export const markNotificationAsRead = async (notificationId) => {
  try {
    const response = await fetch(
      `${BASE_URL}/api/notifications/${notificationId}/read`,
      {
        ...defaultOptions,
        method: "PATCH",
        headers: {
          ...defaultOptions.headers,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Failed to mark notification as read"
      );
    }
  } catch (error) {
    console.error("Error marking notification as read:", error);
    throw error;
  }
};

// Vision Board Layout API functions
export const fetchVisionBoardLayout = async (year) => {
  try {
    const response = await fetch(`${BASE_URL}/vision-board/layout/${year}`, {
      method: "GET",
      headers: {
        ...defaultOptions.headers,
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 401) return;
      // console.error("Error fetching vision board layout:", errorData);
      throw new Error(
        errorData.message || "Failed to fetch vision board layout"
      );
    }

    const data = await response.json();
    // console.log("Fetched layout:", data);
    return data.layout;
  } catch (error) {
    console.error("Error fetching vision board layout:", error);
    throw error;
  }
};

export const saveVisionBoardLayout = async (year, layout) => {
  const response = await fetch(`${BASE_URL}/vision-board/layout/${year}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ layout }),
  });

  if (!response.ok) {
    if (response.status === 401) return;
    console.error("Error saving vision board layout:", response);
    throw new Error("Failed to save vision board layout");
  }

  const data = await response.json();
  return data.layout;
};


// Finance API functions
export const fetchFinanceData = async (year = null) => {
  try {
    const url = year 
      ? `${BASE_URL}/finance/${year}`
      : `${BASE_URL}/finance`;
    
    const response = await fetch(url, {
      method: 'GET',
      ...defaultOptions,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch finance data');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching finance data:', error);
    throw error;
  }
};

export const fetchMonthFinanceData = async (year, month) => {
  try {
    const response = await fetch(`${BASE_URL}/finance/${year}/${month}`, {
      method: 'GET',
      ...defaultOptions,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch month finance data');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching month finance data:', error);
    throw error;
  }
};

export const createOrUpdateFinanceData = async (year, month, currency, budgetData) => {
  try {
    const response = await fetch(`${BASE_URL}/finance`, {
      method: 'POST',
      ...defaultOptions,
      body: JSON.stringify({
        year,
        month,
        currency,
        budgetData
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create/update finance data');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating/updating finance data:', error);
    throw error;
  }
};

export const updateMonthFinanceData = async (year, month, budgetData) => {
  try {
    const response = await fetch(`${BASE_URL}/finance/${year}/${month}`, {
      method: 'PUT',
      ...defaultOptions,
      body: JSON.stringify({
        budgetData
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update month finance data');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating month finance data:', error);
    throw error;
  }
};

export const deleteMonthFinanceData = async (year, month) => {
  try {
    const response = await fetch(`${BASE_URL}/finance/${year}/${month}`, {
      method: 'DELETE',
      ...defaultOptions,
    });

    if (!response.ok) {
      throw new Error('Failed to delete month finance data');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting month finance data:', error);
    throw error;
  }
};

export const deleteYearFinanceData = async (year) => {
  try {
    const response = await fetch(`${BASE_URL}/finance/${year}`, {
      method: 'DELETE',
      ...defaultOptions,
    });

    if (!response.ok) {
      throw new Error('Failed to delete year finance data');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting year finance data:', error);
    throw error;
  }
};
